import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchKontaktInformation } from '../lib/cms';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [kontaktInfo, setKontaktInfo] = useState(null);

  useEffect(() => {
    async function loadInfo() {
      try {
        const info = await fetchKontaktInformation();
        if (info) {
          setKontaktInfo(info);
        }
      } catch (err) {
        console.error('Fejl ved hentning af footer-info:', err);
      }
    }
    loadInfo();
  }, []);

  const telefon = kontaktInfo?.telefon || '22 24 39 90';
  const email = kontaktInfo?.email || 'info@dahliahjoernet.dk';
  const adresse = kontaktInfo?.adresse || 'Grindstedvej 55';
  const postnrBy = kontaktInfo?.postnr_og_by || '7184 Vandel';
  const saeson = kontaktInfo?.saeson_tekst || 'Pluk-selv sæsonen strækker sig fra midten af juli til den første nattefrost.';
  const aabningstider = kontaktInfo?.aabningstider || 'Åbent alle ugens dage fra kl. 08.00 - 20.00';
  const cvr = kontaktInfo?.cvr || '97945268';
  const instagram = kontaktInfo?.instagram_link || 'https://www.instagram.com/dahlia.hjoernet/';

  return (
    <footer className="bg-[#9b4d85] text-white pt-16 pb-8 border-t border-cream-200/20 relative overflow-hidden">
      {/* Flower pattern background overlay */}
      <div 
        className="absolute inset-0 opacity-8 pointer-events-none bg-repeat"
        style={{ 
          backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/dahlia-flower-pattern.png')`,
          backgroundSize: '320px'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative z-10">
        {/* Brand info */}
        <div className="flex flex-col gap-4">
          <Link to="/" className="flex flex-col group">
            <img 
              src="https://assets.auxodev.dk/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/imgi-1-logo2.svg" 
              alt="Dahliahjørnet" 
              className="h-16 w-auto object-contain self-start brightness-0 invert"
            />
          </Link>
          <div className="flex gap-4 mt-2">
            <a 
              href={instagram} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/80 hover:text-white transition-all duration-200 text-2xl"
              aria-label="Følg os på Instagram"
            >
              <i className="fa-brands fa-instagram" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-serif text-lg font-semibold text-white mb-6">Hurtige links</h4>
          <ul className="flex flex-col gap-3 text-sm text-white/80">
            <li>
              <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            </li>
            <li>
              <Link to="/stiklinger" className="hover:text-white transition-colors">Stiklinger</Link>
            </li>
            <li>
              <Link to="/bestil-knolde" className="hover:text-white transition-colors">Bestil knolde</Link>
            </li>
            <li>
              <Link to="/ideer-til-bordpynt-og-dekorationer" className="hover:text-white transition-colors">Bordpynt</Link>
            </li>
            <li>
              <Link to="/arrangementer" className="hover:text-white transition-colors">Arrangementer</Link>
            </li>
            <li>
              <Link to="/galleri" className="hover:text-white transition-colors">Galleri</Link>
            </li>
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h4 className="font-serif text-lg font-semibold text-white mb-6">Kontakt os</h4>
          <ul className="flex flex-col gap-4 text-sm text-white/80">
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-location-dot text-white mt-1" />
              <span>
                {adresse}<br />
                {postnrBy}
              </span>
            </li>
            <li className="flex items-center gap-3">
              <i className="fa-solid fa-phone text-white" />
              <a href={`tel:${telefon.replace(/\s+/g, '')}`} className="hover:text-white transition-colors whitespace-nowrap">
                {telefon}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <i className="fa-solid fa-envelope text-white" />
              <a href={`mailto:${email}`} className="hover:text-white transition-colors whitespace-nowrap">
                {email}
              </a>
            </li>
          </ul>
        </div>

        {/* Opening Hours & App */}
        <div>
          <h4 className="font-serif text-lg font-semibold text-white mb-6">Sæson & Åbningstider</h4>
          <p className="text-sm text-white/80 leading-relaxed mb-4">
            {saeson}
          </p>
          <div className="mt-4">
            <span className="text-xs uppercase tracking-wider text-white font-bold block mb-1">
              Selvbetjening
            </span>
            <span className="text-sm text-white font-medium">
              {aabningstider}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/60 relative z-10">
        <p>© {currentYear} Dahliahjørnet. Alle rettigheder forbeholdes.</p>
        <div className="flex gap-6">
          <Link to="/redigeringsguide" className="hover:text-white transition-colors flex items-center gap-1">
            <i className="fa-solid fa-book-open-reader" />
            Redigeringsguide
          </Link>
          <span>CVR nr.: {cvr}</span>
        </div>
      </div>
    </footer>
  );
}
